import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

/**
 * RequestsTimelineChart - Line chart showing requests over time
 */
const RequestsTimelineChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="h-4 bg-gray-200 rounded w-40 mb-4 animate-pulse"></div>
        <div className="h-64 bg-gray-100 rounded animate-pulse"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Requests Over Time</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No timeline data available
        </div>
      </div>
    );
  }

  // Format data for chart
  const chartData = data.map(item => ({
    time: formatTime(item.time),
    requests: item.requests,
    errors: item.errors,
    avgResponseTime: Math.round((item.avgResponseTime || 0) * 1000)
  }));

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="text-sm font-medium text-gray-700 mb-4">Requests Over Time</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="time"
              tick={{ fontSize: 11 }}
              stroke="#9ca3af"
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 11 }}
              stroke="#9ca3af"
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11 }}
              stroke="#9ca3af"
              tickFormatter={(value) => `${value}ms`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '12px'
              }}
              formatter={(value, name) => {
                if (name === 'avgResponseTime') return [`${value}ms`, 'Avg Response'];
                return [value, name.charAt(0).toUpperCase() + name.slice(1)];
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: '12px' }}
              formatter={(value) => {
                const labels = {
                  requests: 'Requests',
                  errors: 'Errors',
                  avgResponseTime: 'Avg Response (ms)'
                };
                return labels[value] || value;
              }}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="requests"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="errors"
              stroke="#ef4444"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="avgResponseTime"
              stroke="#8b5cf6"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              strokeDasharray="5 5"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/**
 * Format time string for display
 */
function formatTime(timeStr) {
  if (!timeStr) return '';

  // If it's a full ISO date, extract time portion
  if (timeStr.includes('T')) {
    const date = new Date(timeStr);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }

  // If it's already a time string (HH:MM), return as is
  return timeStr;
}

export default RequestsTimelineChart;
